import SharedApi from "../SharedApi";
import UserApi from "../UserApi";

export interface ApiErrorData {
    message?: string;
}

export interface ApiResult<T> {
    data: T | null;
    error: Error | null;
}

export interface FileBlob {
    fileName: string;
    blob: Blob;
}

export const generateUrl = (url: string): string | URL => {
    return SharedApi.generateURL(url);
}

export const getAccessToken = (): string | null => {
    const token = UserApi.getAccessToken();
    if (!token) {
        console.warn("No token found in local storage.");
        return null;
    }
    return token;
}

export const getXSRFToken = async (): Promise<string | null> => {
    const res = apiWrapper<string>(`api/user/antiforgery/token`, 'GET', 'getXSRFToken');
    return res;
}

/*export function apiWrapper<T, Args extends any[]>(context: string, func: (...args: Args) => Promise<Response>): (...args: Args) => Promise<T | null> {
    return async (...args: Args): Promise<T | null> => {
        try {
            const response = await func(...args);
            const data = await handleResponse<T>(response);
            if (data === null) {
                throw new Error(`Received unexpected null response when ${context}`);
            }
            return data;
        } catch (error) {
            throw handleError(error, context);
        }
    }
}*/

export async function apiWrapper<T>(url: string, method: string, context: string, body?: {}, throwErrorOnCatch?: boolean): Promise<T | null> {
    try {
        let opts: any = {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAccessToken()}`
            }
        }
        if (body)
            opts.body = JSON.stringify(body)

        const response = await fetch(generateUrl(url), opts);

        const data = await handleResponse<T>(response);
        if (data === null)
            throw new Error(`Received unexpected null response when ${context}`);

        return data;
    } catch (error) {
        handleError(error, context);

        if (throwErrorOnCatch)
            throw error;

        return null;
    }
}

export async function apiWrapperWithError<T>(url: string, method: string, context: string, body?: {}): Promise<ApiResult<T>> {
    try {
        const data = await apiWrapper<T>(url, method, context, body, true);
        return { data, error: null };
    } catch (error) {
        return { data: null, error: error as Error };
    }
}

export async function apiUploadWithError<T>(url: string, formData: FormData, context: string): Promise<ApiResult<T>> {
    try {
        const opts: RequestInit = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getAccessToken()}`
            },
            body: formData
        };
        const response = await fetch(generateUrl(url), opts);
        const data = await handleResponse<T>(response);
        if (data === null)
            throw new Error(`Received unexpected null response when ${context}`);
        return { data, error: null };
    } catch (error) {
        handleError(error, context);
        return { data: null, error: error as Error };
    }
}

// Regular expression to match ISO 8601 DateTime strings
const isoDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(Z|([+-])(?:[01]\d|2[0-3]):?[0-5]\d)?$/;
// Regular expression to match ISO 8601 DateOnly strings (YYYY-MM-DD)
const isoDateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
// Heuristic for identifying keys that might represent dates by their names
const dateFieldKeyPattern = /(Date|At|On)$|^date([A-Z_])/; // Case-sensitive for suffixes, flexible for prefix

function dateReviver(key: string, value: any): any {
    // Only attempt to convert if the value is a string AND the key suggests it's a date field
    if (typeof value === 'string' && dateFieldKeyPattern.test(key)) {
        // Check for full DateTime ISO string first
        if (isoDateTimePattern.test(value)) {
            return new Date(value);
        }
        // Then check for DateOnly ISO string.
        if (isoDateOnlyPattern.test(value)) {
            // Parse DateOnly strings as UTC midnight
            return new Date(value + 'T00:00:00.000Z');
        }
    }
    // For all other keys or non-string values, or strings that don't match date patterns, return the value as is.
    return value;
}

async function handleResponse<T>(response: Response): Promise<T | null> {
    if (!response.ok) {
        let errorData: ApiErrorData | null = null;
        const errorContentType = response.headers.get("content-type");
        if (errorContentType && errorContentType.includes("application/json")) {
            try {
                const errorJsonText = await response.text();
                if (errorJsonText) {
                    errorData = JSON.parse(errorJsonText, dateReviver) as ApiErrorData;
                }
            } catch (e) {
                console.warn("Could not parse error response body as JSON:", e);
            }
        }

        const errorMessage =
            errorData?.message ||
            `HTTP error! Status: ${response.status} ${response.statusText}`;

        throw new Error(errorMessage);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
        try {
            const jsonText = await response.text();
            if (response.status === 204 || !jsonText) {
                return null;
            }
            return JSON.parse(jsonText, dateReviver) as T;
        } catch (e) {
            console.error("Failed to parse JSON response:", e);
            throw new Error("Failed to parse successful JSON response from server.");
        }
    } else if (contentType && contentType.startsWith("text/")) {
        try {
            const text = await response.text();
            if (response.status === 204 || !text) {
                return null;
            }
            return text as unknown as T;
        } catch (e) {
            console.error("Failed to process text response:", e);
            throw new Error("Failed to process successful text response from server.");
        }
    } else if (contentType && (contentType.includes("application/zip"))) {
        try {
            const blob = await response.blob();
            if (response.status === 204 || !blob) {
                return null;
            }
            const disposition = response.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]*)\1/);
            const fullFileName = match?.[2] ?? 'download.zip';

            // Remove extension (e.g., "report.zip" → "report")
            const fileNameWithoutExt = fullFileName.replace(/\.[^/.]+$/, '');

            const fileBlob: FileBlob = { fileName: fileNameWithoutExt, blob: blob };
            return fileBlob as unknown as T;
        } catch (e) {
            console.error("Failed to process blob response:", e);
            throw new Error("Failed to process successful blob response from server.");
        }
    } else if(contentType && (contentType.includes("application/octet-stream") ||
        contentType.startsWith("image/") || contentType.startsWith("audio/") || contentType.startsWith("video/") ||
        contentType.includes("application/pdf") || contentType.includes("application/doc") || contentType.includes("application/docx") ||
        contentType.startsWith("application/")/* ok to assume everythying except applicaiton/json will be a blob? */)) {
        try {
            const blob = await response.blob();
            if (response.status === 204 || !blob) {
                return null;
            }
            return blob as unknown as T;
        } catch (e) {
            console.error("Failed to process blob response:", e);
            throw new Error("Failed to process successful blob response from server.");
        }
    } else if (response.status === 204 || response.headers.get('Content-Length') === '0') {
        return null;
    } else {
        console.warn(`Response received with status ${response.status} but content type was not application/json or text/plain (or was empty):`, contentType);
        return null;
    }
}

const handleError = (error: unknown, context: string): Error => {
    console.error(`API Error (${context}):`, error);

    if (error instanceof Error) {
        return error;
    } else {
        return new Error(`An unknown error occurred during ${context}: ${String(error)}`);
    }
};
